import type { GeoBrowseStudy } from '../types';

export type ComparisonRef = {
  cui: string;
  accession: string;
  comparison_id: string;
};

export function wholeStudyComparisonId(cui: string, accession: string): string {
  return `${cui}__${accession}`;
}

export function isWholeStudyComparison(row: ComparisonRef): boolean {
  return (
    row.comparison_id === row.accession ||
    row.comparison_id === wholeStudyComparisonId(row.cui, row.accession)
  );
}

export function comparisonRowKey(row: ComparisonRef, prefix?: string): string {
  const key = row.comparison_id || wholeStudyComparisonId(row.cui, row.accession);
  return prefix ? `${prefix}-${key}` : key;
}

export function buildStudyDetailPath(cui: string, accession: string, comparisonId?: string): string {
  const path = `/geo-browse/studies/${encodeURIComponent(cui)}/${encodeURIComponent(accession)}`;
  if (!comparisonId) return path;
  const wholeId = wholeStudyComparisonId(cui, accession);
  if (comparisonId === accession || comparisonId === wholeId) return path;
  return `${path}?comparison_id=${encodeURIComponent(comparisonId)}`;
}

export type StudyStatusVariant = 'clean' | 'warn' | 'error';

export type StudyStatus = {
  label: string;
  variant: StudyStatusVariant;
};

export function getStudyStatus(study: GeoBrowseStudy): StudyStatus {
  if (!study.usable_for_deg) {
    return { label: 'Not usable', variant: 'error' };
  }
  if (study.qc_flags > 0) {
    return {
      label: `${study.qc_flags} QC flag${study.qc_flags === 1 ? '' : 's'}`,
      variant: 'warn',
    };
  }
  return { label: 'Clean', variant: 'clean' };
}

export function ratioBar(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, (value / total) * 100);
}
